import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Tabs } from 'antd';
import styles from './EditDownloadableContent.module.less';
import { useEffect, useRef, useState } from 'react';
import api, { DLCAssetsType, DLCStatus, DownloadableContentDetailDto } from 'api';
import { getCellValue } from 'utils/tableUtils';
import { useSelector } from 'react-redux';
import { projectSelector } from 'redux/selectors/projectSelector';
import Description, { DescriptionRef } from './Description';
import DLCFile, { DLCFileRef } from './DLCFile';
import Assets, { AssetsFormRef } from './Assets';
import ContentRating, { ContentRatingRef } from './ContentRating';
import Pricing, { PricingRef } from './Pricing';
import { getFormattedDateWithTz } from 'utils/dateUtils';
import OmniNotification from 'components/OmniNotification';
import { isNil } from 'lodash';

type ParamsProps = {
	id: string;
};

type FormValidationResult = {
	description: boolean;
	dlcFile: boolean;
	assets: boolean;
	contentRating: boolean;
	pricing: boolean;
};

export interface IDownloadableContentDetailDto extends DownloadableContentDetailDto {
	tabName?: string;
}

export enum DLCTabName {
	Description = 'description',
	DlcFile = 'dlcFile',
	Assets = 'assets',
	ContentRating = 'contentRating',
	Pricing = 'pricing',
}

export default function EditDownloadableContent() {
	const { t } = useTranslation();
	const { id: stringId } = useParams<ParamsProps>();
	const id = Number(stringId);
	const projectInfo = useSelector(projectSelector);
	const [details, setDetails] = useState<IDownloadableContentDetailDto>({});
	const history = useHistory();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formValidationResult, setFormValidationResult] = useState<FormValidationResult>({
		description: false,
		dlcFile: false,
		assets: false,
		contentRating: false,
		pricing: false,
	});

	const descriptionTabPageRef = useRef<DescriptionRef>(null);
	const DLCFileTabPageRef = useRef<DLCFileRef>(null);
	const assetsFormRef = useRef<AssetsFormRef>(null);
	const contentRatingRef = useRef<ContentRatingRef>(null);
	const pcricingRef = useRef<PricingRef>(null);

	const preValidateForm = async () => {
		descriptionTabPageRef.current!.validateDescriptionTab();
		DLCFileTabPageRef.current!.validateDLCFileTab();
		assetsFormRef.current!.validateAssetsFormTab();
		contentRatingRef.current!.validateContentRatingTab();
		pcricingRef.current!.validatePricingTab();

		for (const key in formValidationResult) {
			if (formValidationResult.hasOwnProperty(key) && formValidationResult[key] === false) {
				OmniNotification.error({
					message: t('downloadableContentEdit.message.submit.failure.title'),
					description: t('downloadableContentEdit.message.submit.failure.description'),
				});
				return false;
			}
		}
		return true;
	};

	const getDetails = (tabName?: string) => {
		api.getDLCDetails(id).then((res) => {
			if (tabName) {
				setDetails({
					...res,
					tabName,
				});
			} else {
				setDetails(res);
			}
		});
	};

	useEffect(() => {
		var isDescriptionTabValid = !isNil(details.brief) && !isNil(details.description);
		var isDLCFileValid = true;
		var isDLCAssetsValid = details.assets?.length && !isNil(details.assets.find((item) => item.type == DLCAssetsType.Icon));
		var isContentRatingValid = details.contentRatingConfirmation;
		var isPricingValid = details.pricingConfirmation;
		setFormValidationResult({
			description: isDescriptionTabValid,
			dlcFile: !!isDLCFileValid,
			assets: !!isDLCAssetsValid,
			contentRating: !!isContentRatingValid,
			pricing: !!isPricingValid,
		});
	}, [details]);

	const updateDLCStatusForDLC = () => {
		api
			.updateDLCStatusForDLC({
				id: id,
				status: DLCStatus.Published,
			})
			.then(() => {
				setIsSubmitting(true);
				OmniNotification.success({
					message: t('downloadableContentEdit.message.submit.success.title'),
					description: t('downloadableContentEdit.message.submit.success.description'),
				});
				history.push(`/project/${projectInfo?.id}/game-services/downloadable-content`);
			})
			.catch(() => {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: t('api.defaultError'),
				});
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	useEffect(() => {
		getDetails();
	}, []);

	return (
		<>
			<div className={styles.pageContainer}>
				<div className={styles.buttonContainer}>
					<Button
						type="primary"
						onClick={async () => {
							var result = await preValidateForm();
							if (!result) return;
							updateDLCStatusForDLC();
						}}
						loading={isSubmitting}
						style={{
							marginLeft: 16,
						}}
					>
						{t('common.Submit')}
					</Button>
					<Button
						key="back"
						data-testid="test-cancel"
						className={'color-dark'}
						onClick={() => {
							history.goBack();
						}}
					>
						{t('common.cancel')}
					</Button>
				</div>
				<div className={styles.summaryContainer}>
					<div className={styles.summaryItem}>
						<h3>{t('downloadableContentEdit.summary.createdAt')}</h3>
						<span>{getCellValue(getFormattedDateWithTz(details.createdDate))}</span>
					</div>
					<div className={styles.summaryItem}>
						<h3>{t('downloadableContentEdit.summary.lastUpdatedAt')}</h3>
						<span>{getCellValue(getFormattedDateWithTz(details.lastModifiedDate))}</span>
					</div>
					<div className={styles.summaryItem}>
						<h3>{t('downloadableContentEdit.summary.submittedAt')}</h3>
						<span>
							{details.status != DLCStatus.Published ? '-' : getCellValue(getFormattedDateWithTz(details.lastModifiedDate))}
						</span>
					</div>
				</div>
				<Tabs defaultActiveKey="1" size="large">
					<Tabs.TabPane tab={t('downloadableContentEdit.tabs.description.title')} key="1" forceRender={true}>
						<Description id={id} data={details!} ref={descriptionTabPageRef} onSave={getDetails} />
					</Tabs.TabPane>
					<Tabs.TabPane tab={t('downloadableContentEdit.tabs.dlcFile.title')} key="2" forceRender={true}>
						<DLCFile id={id} data={details!} ref={DLCFileTabPageRef} onSave={getDetails} />
					</Tabs.TabPane>
					<Tabs.TabPane tab={t('downloadableContentEdit.tabs.assets.title')} key="3" forceRender={true}>
						<Assets id={id} data={details!} ref={assetsFormRef} onSave={getDetails} />
					</Tabs.TabPane>
					<Tabs.TabPane tab={t('downloadableContentEdit.tabs.contentRating.title')} key="4" forceRender={true}>
						<ContentRating id={id} data={details!} ref={contentRatingRef} onSave={getDetails} />
					</Tabs.TabPane>
					<Tabs.TabPane tab={t('downloadableContentEdit.tabs.pricing.title')} key="5" forceRender={true}>
						<Pricing id={id} data={details!} ref={pcricingRef} onSave={getDetails} />
					</Tabs.TabPane>
				</Tabs>
			</div>
		</>
	);
}
